export const IDIBU_LOGO_BASE = 'https://idibu.com/images/stories/Portal_logos/';

/**
 * Logo handling
 *
 * The `IdibuPortal.logo` field can be in three forms depending on source:
 *
 *   1. A bare filename (e.g. "indeed.png")  → real Idibu portals
 *   2. A bare domain   (e.g. "indeed.com")  → demo fixtures
 *   3. A full URL      (e.g. "https://…")   → either, used as-is
 *
 * For domain-form entries we build a fallback chain of public, no-auth
 * logo / favicon services. Each `<img>` tries them in order on error,
 * and only after all of them 404 do we render the colored-initials box.
 */

const DOMAIN_RE = /^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;

function looksLikeDomain(value: string): boolean {
    if (!DOMAIN_RE.test(value)) return false;
    // Reject filenames like "indeed.png" — image extensions aren't TLDs.
    return !/\.(png|jpe?g|gif|svg|webp)$/i.test(value);
}

function domainSources(domain: string): string[] {
    return [
        `https://logo.clearbit.com/${domain}`,
        `https://icons.duckduckgo.com/ip3/${domain}.ico`,
        `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
    ];
}

function extractDomain(url: string): string | null {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return null;
    }
}

/**
 * Returns an ordered list of URLs to try for a portal's logo. The image
 * component should walk this list on each `onError` and fall through to
 * a colored-initials box once exhausted.
 */
export function logoCandidates(logo?: string): string[] {
    if (!logo) return [];

    if (/^https?:\/\//i.test(logo)) {
        const domain = extractDomain(logo);
        return domain ? [logo, ...domainSources(domain)] : [logo];
    }

    if (looksLikeDomain(logo)) {
        return domainSources(logo);
    }

    // Idibu CDN filename
    return [`${IDIBU_LOGO_BASE}${logo}`];
}

/**
 * Convenience for callers that just want a single URL — first candidate.
 * New code should prefer `logoCandidates` + an `<img onError>` walker.
 */
export function resolvePortalLogo(logo?: string): string {
    const [first] = logoCandidates(logo);
    return first ?? `${IDIBU_LOGO_BASE}default.png`;
}

/** Stable color from a string — used for the colored-initial fallback. */
export function logoFallbackColor(seed: string): string {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    const hue = Math.abs(h) % 360;
    return `hsl(${hue} 38% 58%)`;
}
